import { CdkCopyToClipboard } from '@angular/cdk/clipboard'
import { CommonModule } from '@angular/common'
import {
  Component,
  DestroyRef,
  Signal,
  TemplateRef,
  computed,
  inject,
  input,
  viewChild,
} from '@angular/core'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'

import { NodeState } from 'libs/sdk/core/src/lib/types/process-instance.dictionary'
import { isEqual } from 'lodash'
import { BreadcrumbModule } from 'primeng/breadcrumb'
import { TooltipModule } from 'primeng/tooltip'

import { BehaviorSubject, debounceTime, distinctUntilChanged, filter, take, tap } from 'rxjs'

import { FlxButtonModule, FlxModalModule, FlxSpinnerModule } from '@flowx/angular-ui-toolkit'
import { ApplicationPermissionsStore } from '@flowx/designer/modules/projects/store/application-permissions.store'
import {
  ExpandableTableColumn,
  ExpandableTableComponent,
} from '@flowx/designer/shared/components/expandable-table/expandable-table.component'
import { JsonViewerComponent } from '@flowx/designer/shared/components/json-viewer/json-viewer.component'
import { ProcessData, TokenDefinition } from '@flowx/designer/shared/entities/process-instance.types'
import { ProcessInstanceService } from '@flowx/designer/shared/services/process-instance.service'
import { ToastMessageService } from '@flowx/designer/shared/services/toast-message.service'
import { TOKEN_STATE } from '@flowx/shared/interfaces'

import { tokenAndNodesStatusTagTypeMap } from './process-instance-tokens.dictionary'
import { buildTokenHierarchy } from './process-instance-tokens.utils'

@Component({
  selector: 'app-process-instance-tokens',
  templateUrl: './process-instance-tokens.component.html',
  imports: [
    CommonModule,
    FlxModalModule,
    FlxSpinnerModule,
    JsonViewerComponent,
    ExpandableTableComponent,
    FlxButtonModule,
    CdkCopyToClipboard,
    BreadcrumbModule,
    TooltipModule,
  ],
  host: { class: 'h-full border border-neutral-100 rounded-2xl block overflow-hidden' },
})
export class ProcessInstanceTokensComponent {
  #processInstanceService = inject(ProcessInstanceService)
  #destroyRef = inject(DestroyRef)
  #applicationPermissionsStore = inject(ApplicationPermissionsStore)
  #toastMessageService = inject(ToastMessageService)
  #copiedUuid = new BehaviorSubject<string | undefined>(undefined)

  uuidTemplate = viewChild<TemplateRef<TokenDefinition>>('uuid')
  tokenStatusTemplate = viewChild<TemplateRef<TOKEN_STATE>>('tokenStatus')
  currentNodeStatusTemplate = viewChild<TemplateRef<NodeState>>('currentNodeStatus')
  tokenDateTemplate = viewChild<TemplateRef<string>>('tokenDate')
  actionsTemplate = viewChild<TemplateRef<TokenDefinition>>('actions')
  process = input<ProcessData>()

  selectedToken = {}
  showTokenModal = false

  canRetry = computed(() => {
    const permissions = this.#applicationPermissionsStore.permissions()
    return !!permissions.process_instances_edit
  })
  retryInProgress = toSignal(this.#processInstanceService.retryInProgress)
  TOKEN_STATE = TOKEN_STATE

  scrollableTokensTableCols: Signal<ExpandableTableColumn[]> = computed(() => {
    const currentNodeStatusTemplate = this.currentNodeStatusTemplate()
    const tokenDateTemplate = this.tokenDateTemplate()
    const actionsTemplate = this.actionsTemplate()

    return [
      {
        field: 'context',
        header: 'Context',
        class: 'w-52',
      },
      {
        field: 'initiatorNode',
        header: 'Initiator Node',
        class: 'w-52',
      },
      {
        field: 'firstExecutedNode',
        header: 'First Executed Node',
        class: 'w-52',
      },
      {
        field: 'currentNode',
        header: 'Current Node',
        class: 'w-52',
      },
      {
        field: 'statusCurrentNode',
        header: 'Current Node Status',
        template: currentNodeStatusTemplate,
        class: 'w-52',
      },

      {
        field: 'dateUpdated',
        header: 'Last updated',
        template: tokenDateTemplate,
        class: 'w-60',
      },
      {
        header: '',
        field: 'actions',
        template: actionsTemplate,
        templateClass: 'py-2',
        class: 'w-28',
      },
    ]
  })

  frozenColumns: Signal<ExpandableTableColumn[]> = computed(() => {
    const uuidTemplate = this.uuidTemplate()
    const tokenStatusTemplate = this.tokenStatusTemplate()
    return [
      {
        field: 'uuid',
        header: 'Token uuid',
        width: 208,
        template: uuidTemplate,
      },
      {
        field: 'state',
        header: 'Token Status',
        template: tokenStatusTemplate,
        width: 144,
      },
    ]
  })

  frozenColumnsWidth = computed(() => {
    const frozenColumns = this.frozenColumns()
    return `${frozenColumns.reduce((total, col) => total + col.width, 0)}px`
  })

  hierarchicalTokens = computed(() => {
    const process = this.process()
    return buildTokenHierarchy(process)
  })

  expandableFields = ['uuid']
  TokenAndNodesStatusTagTypeMap = tokenAndNodesStatusTagTypeMap

  constructor() {
    this.#copiedUuid
      .pipe(
        distinctUntilChanged(isEqual),
        filter(Boolean),
        tap((uuid) => {
          this.#toastMessageService.showSuccessMessage(`Full Token UUID copied to clipboard.`, uuid)
        }),
        debounceTime(2000),
        tap(() => this.#copiedUuid.next(undefined)),
        takeUntilDestroyed()
      )
      .subscribe()
  }

  showTokenDetailsModal(token: any): void {
    this.selectedToken = token
    this.showTokenModal = true
  }

  hideTokenDetailsModal(): void {
    this.showTokenModal = false
    this.selectedToken = {}
  }

  retryToken(token: any): void {
    this.#processInstanceService
      .retryProcessing(this.process().instance.uuid, token.uuid)
      .pipe(take(1), takeUntilDestroyed(this.#destroyRef))
      .subscribe()
  }

  onCopyToClipboardSuccess(uuid: string): void {
    this.#copiedUuid.next(uuid)
  }
}
